import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import appConfig from "../config/appConfig";

const AUTH_SIGNIN_PATH = process.env.REACT_APP_AUTH_SIGNIN_PATH || "/signin";
const AUTH_APP_URL = appConfig.authAppUrl || process.env.REACT_APP_AUTH_APP_URL || "";
const AUTH_HOST_STORAGE_KEY = "clinicAuthOrigin";
const AUTH_CANDIDATE_PORTS = [3000, 3001, 3002, 3003, 3004, 3005, 3006, 3007];

const isLocalEnvironment = () => {
  if (typeof window === "undefined") return false;
  return Boolean(window.location.hostname);
};

const normalizeAuthOrigin = (value) => {
  if (!value) return "";
  try {
    return new URL(value).origin;
  } catch {
    try {
      return new URL(`http://${value}`).origin;
    } catch {
      return "";
    }
  }
};

const buildAuthUrl = (origin) => {
  if (!origin) return "";
  try {
    return new URL(AUTH_SIGNIN_PATH, origin).toString();
  } catch {
    return `${origin.replace(/\/$/, "")}/${AUTH_SIGNIN_PATH.replace(/^\//, "")}`;
  }
};

const probeAuthOrigin = async (origin) => {
  try {
    await fetch(new URL(AUTH_SIGNIN_PATH, origin).toString(), {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
    });
    return true;
  } catch {
    return false;
  }
};

const discoverLocalAuthOrigin = async (currentOrigin) => {
  const protocol = typeof window !== "undefined" && window.location.protocol ? window.location.protocol : "http:";
  const hostname = typeof window !== "undefined" && window.location.hostname ? window.location.hostname : "";
  const candidates = hostname
    ? AUTH_CANDIDATE_PORTS
        .map((port) => `${protocol}//${hostname}:${port}`)
        .filter((origin) => origin !== currentOrigin)
    : [];

  for (const candidate of candidates) {
    if (await probeAuthOrigin(candidate)) {
      return candidate;
    }
  }

  return "";
};

const DASHBOARD_PATH = process.env.REACT_APP_DASHBOARD_PATH || "/admin-dashboard";
const DASHBOARD_URL = (() => {
  const base = appConfig.dashboardUrl || process.env.REACT_APP_DASHBOARD_URL || (typeof window !== "undefined" && isLocalEnvironment() ? window.location.origin : "");
  const normalizedBase = base.replace(/\/$/, "");
  if (normalizedBase.endsWith(DASHBOARD_PATH)) {
    return normalizedBase;
  }
  return `${normalizedBase}${DASHBOARD_PATH}`;
})();

const AppPortal = () => {
  const { auth, refresh } = useAuth();
  const [authUrl, setAuthUrl] = useState("");
  const [authDiscovering, setAuthDiscovering] = useState(true);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;

    const initAuth = async () => {
      const currentOrigin = window.location.origin;
      let resolvedOrigin = normalizeAuthOrigin(AUTH_APP_URL);

      if (!resolvedOrigin) {
        resolvedOrigin = window.localStorage.getItem(AUTH_HOST_STORAGE_KEY) || "";
      }

      let isResolvedOriginValid = false;
      if (resolvedOrigin) {
        isResolvedOriginValid = await probeAuthOrigin(resolvedOrigin);
      }

      if (!isResolvedOriginValid && isLocalEnvironment()) {
        const discovered = await discoverLocalAuthOrigin(currentOrigin);
        if (discovered) {
          resolvedOrigin = discovered;
          isResolvedOriginValid = true;
        }
      }

          if (!resolvedOrigin || resolvedOrigin === currentOrigin || !isResolvedOriginValid) {
        setAuthError(
          "No separate auth host is configured or reachable. Set REACT_APP_AUTH_APP_URL to your auth origin, verify the auth app is running, or use the app once so the login host is stored."
        );
        if (resolvedOrigin === currentOrigin) {
          window.localStorage.removeItem(AUTH_HOST_STORAGE_KEY);
        }
        setAuthUrl("");
        setAuthDiscovering(false);
        return;
      }

      window.localStorage.setItem(AUTH_HOST_STORAGE_KEY, resolvedOrigin);
      setAuthUrl(buildAuthUrl(resolvedOrigin));
      setAuthError("");
      setAuthDiscovering(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleWindowFocus = () => {
      if (!auth.loading && !auth.authenticated) {
        refresh();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [auth.loading, auth.authenticated, refresh]);

  const redirectToAuth = () => {
    if (typeof window === "undefined" || !authUrl) return;
    window.location.href = authUrl;
  };

  const redirectToDashboard = () => {
    if (typeof window === "undefined") return;
    window.location.href = DASHBOARD_URL;
  };

  const isSignedIn = auth?.authenticated && auth?.clinic?.id;
  const actionLabel = isSignedIn
    ? "Go to app dashboard"
    : authDiscovering
      ? "Locating auth service…"
      : authUrl
        ? "Login to access app"
        : "Auth host not configured";
  const actionDisabled = authDiscovering || (!isSignedIn && !authUrl);
  const panelCopy = isSignedIn
    ? "Your clinic session is active. Continue to the dashboard safely."
    : "Authenticate through the clinic auth service before continuing to the dashboard.";

  // Presentational-only status variant driving the pulse strip + stamp + accent color.
  // Derived purely from existing state — no new auth/routing behavior.
  const statusVariant = auth?.loading
    ? "checking"
    : isSignedIn
      ? "stable"
      : authError
        ? "alert"
        : "waiting";

  const statusReadout = auth?.loading
    ? "READING SESSION"
    : isSignedIn
      ? "SESSION STABLE"
      : authError
        ? "SIGNAL LOST"
        : "AWAITING LOGIN";

  const stampText = auth?.loading
    ? "VERIFYING"
    : isSignedIn
      ? "VERIFIED"
      : authError
        ? "UNREACHABLE"
        : "PENDING";

  const referenceValue = isSignedIn ? String(auth.clinic.id).slice(0, 10) : "—";

  const portalStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');

    :root {
      --paper: #eef1ea;
      --paper-deep: #e3e7dc;
      --card: #fbfaf6;
      --ink: #1c2a22;
      --ink-soft: #4b5c4f;
      --ink-faint: #7c8b7f;
      --line: rgba(28, 42, 34, 0.14);
      --pine: #1f5d45;
      --pine-deep: #123c2c;
      --pine-soft: rgba(31, 93, 69, 0.12);
      --amber: #b8792c;
      --amber-soft: rgba(184, 121, 44, 0.14);
      --rust: #9c4430;
      --rust-soft: rgba(156, 68, 48, 0.13);
    }

    .portal-shell {
      min-height: 100vh;
      width: 100%;
      background:
        repeating-linear-gradient(0deg, transparent 0 39px, rgba(28, 42, 34, 0.035) 39px 40px),
        repeating-linear-gradient(90deg, transparent 0 39px, rgba(28, 42, 34, 0.035) 39px 40px),
        var(--paper);
      color: var(--ink);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: clamp(16px, 5vw, 48px);
      font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      box-sizing: border-box;
    }

    .portal-shell *,
    .portal-shell *::before,
    .portal-shell *::after {
      box-sizing: border-box;
    }

    .portal-card {
      position: relative;
      width: min(560px, 100%);
      background: var(--card);
      border-radius: 6px;
      border: 1px solid var(--line);
      box-shadow: 0 30px 70px -40px rgba(18, 30, 22, 0.45), 0 2px 0 var(--paper-deep);
      overflow: hidden;
    }

    /* --- Signature element: EKG pulse strip on graph-paper ground --- */
    .pulse-strip {
      position: relative;
      background:
        repeating-linear-gradient(0deg, transparent 0 9px, rgba(28, 42, 34, 0.06) 9px 10px),
        repeating-linear-gradient(90deg, transparent 0 9px, rgba(28, 42, 34, 0.06) 9px 10px),
        var(--paper-deep);
      padding: 16px clamp(20px, 5vw, 32px) 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      border-bottom: 1px solid var(--line);
    }

    .pulse-strip svg {
      display: block;
      width: 100%;
      height: 40px;
    }

    .pulse-path {
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke: var(--pulse-color, var(--pine));
      stroke-dasharray: 7 5;
      animation: pulse-scan 2.6s linear infinite;
    }

    [data-status="stable"] .pulse-path { --pulse-color: var(--pine); animation-duration: 3.4s; }
    [data-status="checking"] .pulse-path { --pulse-color: var(--ink-soft); animation-duration: 1.3s; }
    [data-status="waiting"] .pulse-path { --pulse-color: var(--amber); animation-duration: 2.2s; }
    [data-status="alert"] .pulse-path { --pulse-color: var(--rust); animation-duration: 1s; }

    @keyframes pulse-scan {
      to { stroke-dashoffset: -230; }
    }

    .pulse-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 0.7rem;
      letter-spacing: 0.08em;
      color: var(--ink-faint);
    }

    .pulse-meta .readout {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      color: var(--pulse-color, var(--pine));
      font-weight: 500;
    }

    .readout-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    [data-status="checking"] .readout-dot,
    [data-status="waiting"] .readout-dot {
      animation: dot-pulse 1.6s ease-in-out infinite;
    }

    @keyframes dot-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }

    /* --- Ink stamp badge, the page's signature mark --- */
    .stamp {
      position: absolute;
      top: 70px;
      right: clamp(16px, 4vw, 30px);
      transform: rotate(-6deg);
      padding: 7px 14px;
      border: 2px solid var(--stamp-color, var(--pine));
      border-radius: 3px;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.16em;
      color: var(--stamp-color, var(--pine));
      background: rgba(255, 255, 255, 0.5);
      pointer-events: none;
      opacity: 0.9;
    }

    .stamp::before {
      content: "";
      position: absolute;
      inset: 3px;
      border: 1px solid var(--stamp-color, var(--pine));
      border-radius: 2px;
    }

    [data-status="stable"] .stamp { --stamp-color: var(--pine); }
    [data-status="checking"] .stamp { --stamp-color: var(--ink-soft); }
    [data-status="waiting"] .stamp { --stamp-color: var(--amber); }
    [data-status="alert"] .stamp { --stamp-color: var(--rust); }

    /* --- Hero / heading --- */
    .portal-hero {
      padding: clamp(22px, 5vw, 32px) clamp(20px, 5vw, 32px) 4px;
    }

    .hero-eyebrow {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 12px;
      font-family: 'IBM Plex Mono', monospace;
      font-size: 0.7rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--ink-faint);
      margin-bottom: 14px;
      padding-bottom: 10px;
      border-bottom: 1px dashed var(--line);
    }

    .hero-eyebrow .ref-value {
      color: var(--ink-soft);
      font-weight: 500;
    }

    .portal-hero h1 {
      margin: 0;
      font-family: 'Fraunces', 'IBM Plex Sans', serif;
      font-optical-sizing: auto;
      font-weight: 600;
      font-size: clamp(1.6rem, 4.6vw, 2.15rem);
      line-height: 1.14;
      letter-spacing: -0.01em;
      color: var(--pine-deep);
    }

    .portal-hero p {
      margin: 12px 0 0;
      color: var(--ink-soft);
      font-size: 0.98rem;
      line-height: 1.65;
      max-width: 46ch;
    }

    /* --- Body / panel --- */
    .portal-body {
      padding: clamp(20px, 5vw, 32px);
      display: grid;
      gap: 16px;
    }

    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 9px;
      padding: 8px 14px;
      border-radius: 999px;
      font-weight: 600;
      font-size: 0.82rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border: 1px solid transparent;
      width: fit-content;
    }

    .status-pill.authenticated {
      background: var(--pine-soft);
      color: var(--pine-deep);
      border-color: rgba(31, 93, 69, 0.3);
    }

    .status-pill.unauthenticated {
      background: var(--rust-soft);
      color: var(--rust);
      border-color: rgba(156, 68, 48, 0.28);
    }

    .status-pill .dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
    }

    .panel {
      padding: clamp(16px, 4vw, 22px);
      border-radius: 5px;
      background: var(--paper);
      border: 1px solid var(--line);
      display: grid;
      gap: 10px;
    }

    .panel h2 {
      margin: 0;
      font-family: 'Fraunces', serif;
      font-size: 1.05rem;
      font-weight: 600;
      color: var(--pine-deep);
    }

    .panel p {
      margin: 0;
      color: var(--ink-soft);
      line-height: 1.7;
      font-size: 0.94rem;
    }

    .clinic-name {
      color: var(--pine-deep);
      font-weight: 700;
    }

    .error-message {
      margin: 2px 0 0;
      padding: 12px 14px;
      border-radius: 5px;
      background: var(--amber-soft);
      color: #4a3413;
      font-size: 0.86rem;
      line-height: 1.65;
      border: 1px solid rgba(184, 121, 44, 0.3);
    }

    /* --- Actions --- */
    .action-group {
      display: grid;
      gap: 10px;
    }

    .primary-button,
    .secondary-button {
      width: 100%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 13px 18px;
      border-radius: 5px;
      font-size: 0.94rem;
      font-weight: 600;
      font-family: 'IBM Plex Sans', sans-serif;
      letter-spacing: 0.01em;
      transition: transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease, border-color 150ms ease;
      border: 1px solid transparent;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .primary-button {
      color: #fbfaf6;
      box-shadow: 0 14px 30px -16px rgba(18, 30, 22, 0.5);
    }

    .primary-button.confirmed {
      background: var(--pine);
      border-color: var(--pine-deep);
    }

    .primary-button.confirmed:hover:not(:disabled) {
      background: var(--pine-deep);
      transform: translateY(-1px);
    }

    .primary-button.unconfirmed {
      background: var(--rust);
      border-color: #7c3222;
    }

    .primary-button.unconfirmed:hover:not(:disabled) {
      background: #7c3222;
      transform: translateY(-1px);
    }

    .primary-button:active:not(:disabled) {
      transform: translateY(0);
    }

    .secondary-button {
      background: transparent;
      color: var(--ink-soft);
      border-color: var(--line);
    }

    .secondary-button:hover:not(:disabled) {
      background: var(--paper);
      border-color: var(--ink-faint);
      color: var(--ink);
    }

    .primary-button:disabled,
    .secondary-button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    .primary-button:focus-visible,
    .secondary-button:focus-visible {
      outline: 2px solid var(--pine);
      outline-offset: 2px;
    }

    @media (max-width: 420px) {
      .portal-hero, .portal-body {
        padding-left: 18px;
        padding-right: 18px;
      }
      .stamp {
        top: 62px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .pulse-path,
      .readout-dot {
        animation: none !important;
      }
    }
  `;

  const PulseStrip = () => (
    <div className="pulse-strip">
      <svg viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
        <path
          className="pulse-path"
          d="M0,20 L58,20 L70,20 L80,4 L90,36 L100,12 L112,20 L158,20 L170,20 L180,8 L190,32 L200,20 L400,20"
        />
      </svg>
      <div className="pulse-meta">
        <span>CLINIC ACCESS · CHART LOG</span>
        <span className="readout">
          <span className="readout-dot" />
          {statusReadout}
        </span>
      </div>
    </div>
  );

  const Stamp = () => (
    <div className="stamp" aria-hidden="true">
      {stampText}
    </div>
  );

  if (auth?.loading) {
    return (
      <div className="portal-shell">
        <style>{portalStyles}</style>
        <div className="portal-card" data-status="checking" aria-busy="true" aria-live="polite">
          <PulseStrip />
          <Stamp />
          <div className="portal-hero">
            <div className="hero-eyebrow">
              <span>Session check</span>
              <span className="ref-value">REF {referenceValue}</span>
            </div>
            <h1>Checking your session</h1>
            <p>Please wait a moment while we confirm whether you're already signed in.</p>
          </div>
          <div className="portal-body">
            <div className="panel">
              <h2>Loading session</h2>
              <p>We are verifying whether you are already signed in through the clinic authentication service.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (auth?.authenticated && auth?.clinic?.id) {
    return (
      <div className="portal-shell">
        <style>{portalStyles}</style>
        <div className="portal-card" data-status="stable">
          <PulseStrip />
          <Stamp />
          <div className="portal-hero">
            <div className="hero-eyebrow">
              <span>Access confirmed</span>
              <span className="ref-value">REF {referenceValue}</span>
            </div>
            <h1>Your clinic is ready</h1>
            <p>Authentication successful. Continue to the dashboard when you're ready.</p>
          </div>
          <div className="portal-body">
            <div className="panel">
              <span className="status-pill authenticated">
                <span className="dot" />
                Signed in as <span className="clinic-name">{auth.clinic.name}</span>
              </span>
              <h2>Dashboard ready</h2>
              <p>Your session is active. Continue to the clinic dashboard on the correct application host.</p>
            </div>

            <div className="action-group">
              <button className="primary-button confirmed" onClick={redirectToDashboard}>
                Go to dashboard
              </button>
              <button className="secondary-button" onClick={redirectToAuth}>
                Sign in again / switch account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-shell">
      <style>{portalStyles}</style>
      <div className="portal-card" data-status={statusVariant}>
        <PulseStrip />
        <Stamp />
        <div className="portal-hero">
          <div className="hero-eyebrow">
            <span>Enterprise access</span>
            <span className="ref-value">REF {referenceValue}</span>
          </div>
          <h1>Clinic access portal</h1>
          <p>A secure authentication gateway for your clinic dashboard experience.</p>
        </div>
        <div className="portal-body">
          <div className="panel">
            <span className={`status-pill ${isSignedIn ? "authenticated" : "unauthenticated"}`}>
              <span className="dot" />
              {isSignedIn ? "Signed in" : "Not signed in"}
            </span>
            <h2>{isSignedIn ? "Clinic dashboard ready" : "Begin secure login"}</h2>
            <p>{panelCopy}</p>
            {authError && !isSignedIn ? <p className="error-message">{authError}</p> : null}
          </div>

          <div className="action-group">
            <button
              className={`primary-button ${isSignedIn ? "confirmed" : "unconfirmed"}`}
              onClick={isSignedIn ? redirectToDashboard : redirectToAuth}
              disabled={actionDisabled}
            >
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppPortal;